/**
 * Request payloads built from the page form.
 *
 * The route is never typed: it comes from the dashboard catalog for the page's slug, so the BFF
 * stores where that slug actually resolves. Edit sends it too, which fills in pages saved before the
 * form sent one. A slug outside the catalog sends no route, leaving whatever the BFF has untouched.
 *
 * @module Pages/BUs/Pages
 */

import type { PageFormValues } from '../Components/PageFormModal/PageFormModal';
import type { Page, PageCreatePayload, PageUpdatePayload } from '../Types/Pages.types';
import { findCatalogPage } from '@/Shared/brand';

/** `{ route }` when the slug is in the tenant's catalog, nothing otherwise. */
function catalogRoute(tenantSlug: string, slug: string): { route?: string } {
  const route = findCatalogPage(tenantSlug, slug)?.route;
  return route ? { route } : {};
}

/** Payload for `POST /pages`. */
export function buildCreatePagePayload(values: PageFormValues): PageCreatePayload {
  return {
    tenantSlug: values.tenantSlug,
    slug: values.slug,
    ...catalogRoute(values.tenantSlug, values.slug),
    name: values.name,
    isMain: values.isMain,
    isActive: values.isActive,
  };
}

/**
 * Payload for `PATCH /pages/:id`. The route is looked up from the page being edited: its tenant
 * and slug cannot change on edit.
 */
export function buildUpdatePagePayload(page: Page, values: PageFormValues): PageUpdatePayload {
  return {
    name: values.name,
    ...catalogRoute(page.tenantSlug, page.slug),
    isMain: values.isMain,
    isActive: values.isActive,
  };
}
